import React, { useEffect, useState } from 'react';
import { ChevronLeft, Search, ArrowUpRight, Building, Award, Star, CheckCircle, XCircle, Clock, Users, TrendingUp } from 'lucide-react';
import { BottomNav } from '../components/BottomNav';
interface DeveloperNetworkPageProps {
  onBack: () => void;
  onBrokerageProfileClick?: (brokerId: number, brokerName: string, isInNetwork: boolean) => void;
  onNetworkClick?: () => void;
  onInventoryClick?: () => void;
  onRMIClick?: () => void;
  onProfileClick?: () => void;
}
interface Brokerage {
  id: number;
  name: string;
  logo: string;
  email: string;
  phone: string;
  agentsCount: number;
  dealsCount: number;
  category: 'my-brokerages' | 'suggested';
  memberships: {
    gold: number;
    silver: number;
    bronze: number;
  };
  totalMemberships: number;
  rating: number;
  specialization: string;
  establishedYear: number;
  officeLocations: string[];
}
interface AccreditationRequest {
  id: number;
  brokerageName: string;
  brokerageLogo: string;
  email: string;
  contactPerson: string;
  requestedTier: 'gold' | 'silver' | 'bronze';
  requestDate: string;
  status: 'pending' | 'approved' | 'rejected';
  agentsCount: number;
  dealsCompleted: number;
  message: string;
}
export function DeveloperNetworkPage({
  onBack,
  onBrokerageProfileClick,
  onNetworkClick,
  onInventoryClick,
  onRMIClick,
  onProfileClick
}: DeveloperNetworkPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Brokerage logos
  const brokerageLogo1 = 'https://cdn.magicpatterns.com/uploads/6BgvouriBk6paAGWqLe7NS/OIP.1Lbk_6fxHaaU9UwfINN2JAAAAA.webp';
  const brokerageLogo2 = 'https://cdn.magicpatterns.com/uploads/jZaBreBdfYsDWTGSDxyK6a/OIP.FOaAYWJH6ikQ-svnuEwxwwHaEo.webp';
  const brokerageLogo3 = 'https://cdn.magicpatterns.com/uploads/niPHF2enxAVm3q3inB94mf/OIP.KgNwAD0NUiClwWnlKKGHXAAAAA.webp';
  const brokerageLogo4 = 'https://cdn.magicpatterns.com/uploads/2m6PX3t2LTb47QV7aD5ZWH/OIP.LKSfM12Q4CXDf4ucVM1RbgHaHa.webp';
  const brokerageLogo5 = 'https://cdn.magicpatterns.com/uploads/cNZRbMg6yCRYutpQ9xSnL9/OIP.LsnAiW6moxtg5fFjG12K5AHaHa.webp';
  // Mock accreditation requests data
  const [accreditationRequests] = useState<AccreditationRequest[]>([{
    id: 1,
    brokerageName: 'Real Estate Investment Group',
    brokerageLogo: brokerageLogo1,
    email: 'partnerships@reig.com',
    contactPerson: 'Ahmed Hassan - Managing Director',
    requestedTier: 'gold',
    requestDate: '2 hours ago',
    status: 'pending',
    agentsCount: 45,
    dealsCompleted: 230,
    message: 'We have successfully closed 230 deals with 45 certified agents and would like to establish a Gold Partnership.'
  }, {
    id: 2,
    brokerageName: 'One Broker Group',
    brokerageLogo: brokerageLogo2,
    email: 'info@onebrokergroup.com',
    contactPerson: 'Sarah Mohamed - CEO',
    requestedTier: 'silver',
    requestDate: '5 hours ago',
    status: 'pending',
    agentsCount: 28,
    dealsCompleted: 145,
    message: 'Our brokerage specializes in luxury properties and seeks Silver Partnership to expand collaboration.'
  }, {
    id: 3,
    brokerageName: 'Alliance Brokers Realty',
    brokerageLogo: brokerageLogo3,
    email: 'contact@alliancebrokers.com',
    contactPerson: 'Karim Mahmoud - Director',
    requestedTier: 'bronze',
    requestDate: '1 day ago',
    status: 'pending',
    agentsCount: 15,
    dealsCompleted: 68,
    message: 'Looking forward to partnering with your developments. We have strong presence in New Cairo area.'
  }]);
  // Mock brokerages data
  const [brokerages] = useState<Brokerage[]>([
  // My Brokerages - High Tier
  {
    id: 1,
    name: 'Real Estate Investment Group',
    logo: brokerageLogo1,
    email: 'partnerships@reig.com',
    phone: '+20 2 123 456 7890',
    agentsCount: 45,
    dealsCount: 230,
    category: 'my-brokerages',
    memberships: {
      gold: 5,
      silver: 3,
      bronze: 2
    },
    totalMemberships: 10,
    rating: 4.9,
    specialization: 'Luxury & Commercial Properties',
    establishedYear: 2010,
    officeLocations: ['New Cairo', 'Sheikh Zayed', '6th of October']
  }, {
    id: 2,
    name: 'One Broker Group',
    logo: brokerageLogo2,
    email: 'info@onebrokergroup.com',
    phone: '+20 2 111 222 3333',
    agentsCount: 38,
    dealsCount: 195,
    category: 'my-brokerages',
    memberships: {
      gold: 4,
      silver: 4,
      bronze: 1
    },
    totalMemberships: 9,
    rating: 4.8,
    specialization: 'Residential & Investment',
    establishedYear: 2012,
    officeLocations: ['Maadi', 'Zamalek', 'Heliopolis']
  }, {
    id: 3,
    name: 'Alliance Brokers Realty',
    logo: brokerageLogo3,
    email: 'contact@alliancebrokers.com',
    phone: '+20 2 100 555 6666',
    agentsCount: 52,
    dealsCount: 280,
    category: 'my-brokerages',
    memberships: {
      gold: 3,
      silver: 5,
      bronze: 3
    },
    totalMemberships: 11,
    rating: 4.7,
    specialization: 'New Developments & Compounds',
    establishedYear: 2008,
    officeLocations: ['New Cairo', 'New Capital', 'Shorouk City']
  }, {
    id: 4,
    name: 'RGM Commercial Real Estate',
    logo: brokerageLogo4,
    email: 'info@rgmcommercial.com',
    phone: '+20 2 101 999 0000',
    agentsCount: 35,
    dealsCount: 168,
    category: 'my-brokerages',
    memberships: {
      gold: 2,
      silver: 4,
      bronze: 4
    },
    totalMemberships: 10,
    rating: 4.6,
    specialization: 'Commercial & Office Spaces',
    establishedYear: 2015,
    officeLocations: ['Downtown Cairo', 'New Cairo']
  }, {
    id: 5,
    name: 'MG Real Estate & Investments',
    logo: brokerageLogo5,
    email: 'partnerships@mgrealestate.eg',
    phone: '+20 2 122 777 8888',
    agentsCount: 42,
    dealsCount: 210,
    category: 'my-brokerages',
    memberships: {
      gold: 2,
      silver: 3,
      bronze: 5
    },
    totalMemberships: 10,
    rating: 4.5,
    specialization: 'Investment Properties',
    establishedYear: 2013,
    officeLocations: ['New Capital', 'Smart Village', '5th Settlement']
  }, {
    id: 6,
    name: 'Prime Realty Partners',
    logo: brokerageLogo1,
    email: 'contact@primerealty.eg',
    phone: '+20 2 155 444 3333',
    agentsCount: 30,
    dealsCount: 142,
    category: 'my-brokerages',
    memberships: {
      gold: 1,
      silver: 3,
      bronze: 4
    },
    totalMemberships: 8,
    rating: 4.4,
    specialization: 'High-End Villas & Penthouses',
    establishedYear: 2016,
    officeLocations: ['New Cairo', 'North Coast']
  },
  // Suggested Brokerages
  {
    id: 7,
    name: 'Metropolitan Real Estate',
    logo: brokerageLogo2,
    email: 'info@metropolitan.eg',
    phone: '+20 2 100 111 2222',
    agentsCount: 48,
    dealsCount: 225,
    category: 'suggested',
    memberships: {
      gold: 3,
      silver: 4,
      bronze: 2
    },
    totalMemberships: 9,
    rating: 4.7,
    specialization: 'Mixed-Use Developments',
    establishedYear: 2011,
    officeLocations: ['6th of October', 'Sheikh Zayed']
  }, {
    id: 8,
    name: 'Urban Properties Network',
    logo: brokerageLogo3,
    email: 'partnerships@urbanproperties.com',
    phone: '+20 2 122 333 4444',
    agentsCount: 40,
    dealsCount: 188,
    category: 'suggested',
    memberships: {
      gold: 2,
      silver: 5,
      bronze: 3
    },
    totalMemberships: 10,
    rating: 4.6,
    specialization: 'Urban & Suburban Developments',
    establishedYear: 2014,
    officeLocations: ['New Cairo', 'Madinaty', 'Rehab']
  }, {
    id: 9,
    name: 'Investment Realty Partners',
    logo: brokerageLogo4,
    email: 'contact@investmentrealty.eg',
    phone: '+20 2 111 555 6666',
    agentsCount: 33,
    dealsCount: 156,
    category: 'suggested',
    memberships: {
      gold: 2,
      silver: 3,
      bronze: 4
    },
    totalMemberships: 9,
    rating: 4.5,
    specialization: 'Investment & ROI Properties',
    establishedYear: 2017,
    officeLocations: ['New Capital', 'New Cairo']
  }, {
    id: 10,
    name: 'Coastal Properties Group',
    logo: brokerageLogo5,
    email: 'info@coastalproperties.eg',
    phone: '+20 2 100 777 8888',
    agentsCount: 25,
    dealsCount: 118,
    category: 'suggested',
    memberships: {
      gold: 1,
      silver: 4,
      bronze: 3
    },
    totalMemberships: 8,
    rating: 4.4,
    specialization: 'North Coast & Resort Properties',
    establishedYear: 2015,
    officeLocations: ['North Coast', 'Ain Sokhna', 'Cairo']
  }]);
  const myBrokerages = brokerages.filter(brokerage => brokerage.category === 'my-brokerages' && brokerage.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const suggestedBrokerages = brokerages.filter(brokerage => brokerage.category === 'suggested' && brokerage.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const getTierBadgeColor = (tier: 'gold' | 'silver' | 'bronze') => {
    switch (tier) {
      case 'gold':
        return 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white';
      case 'silver':
        return 'bg-gradient-to-r from-gray-400 to-gray-500 text-white';
      case 'bronze':
        return 'bg-gradient-to-r from-orange-600 to-orange-700 text-white';
    }
  };
  const getMembershipBadges = (memberships: {
    gold: number;
    silver: number;
    bronze: number;
  }) => {
    return <div className="flex items-center gap-1 flex-wrap" data-id="element-1552">
        {memberships.gold > 0 && <div className="flex items-center gap-1 bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded-full text-xs font-bold" data-id="element-1553">
            <Award className="w-3 h-3" data-id="element-1554" />
            {memberships.gold}
          </div>}
        {memberships.silver > 0 && <div className="flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full text-xs font-bold" data-id="element-1555">
            <Award className="w-3 h-3" data-id="element-1556" />
            {memberships.silver}
          </div>}
        {memberships.bronze > 0 && <div className="flex items-center gap-1 bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full text-xs font-bold" data-id="element-1557">
            <Award className="w-3 h-3" data-id="element-1558" />
            {memberships.bronze}
          </div>}
      </div>;
  };
  const handleApproveRequest = (requestId: number) => {
    alert('Accreditation request approved!');
    // In real app, update the request status
  };
  const handleRejectRequest = (requestId: number) => {
    alert('Accreditation request rejected');
    // In real app, update the request status
  };
  const currentRequest = accreditationRequests[currentSlide];
  return <div className="min-h-screen bg-neutral-bg pb-20 font-sans w-full" data-id="element-1559">
      <div className="w-full mx-auto bg-neutral-bg min-h-screen relative" data-id="element-1560">
        {/* Header */}
        <div className="bg-primary px-4 py-4 safe-top flex items-center justify-between sticky top-0 z-10 shadow-sm" data-id="element-1561">
          <button onClick={onBack} className="p-2 -ml-2 text-white hover:bg-white/10 rounded-full transition-colors" data-id="element-1562">
            <ChevronLeft className="w-6 h-6" data-id="element-1563" />
          </button>
          <h1 className="font-semibold text-lg text-white" data-id="element-1564">
            Brokerage Network
          </h1>
          <div className="w-10" data-id="element-1565"></div>
        </div>

        {/* Accreditation Requests Slider */}
        <div className="px-4 pt-6 pb-4" data-id="element-1566">
          <div className="flex items-center justify-between mb-3" data-id="element-1567">
            <h2 className="text-base font-bold text-neutral-text" data-id="element-1568">
              Pending Accreditation Requests
            </h2>
            <span className="bg-accent text-primary text-xs font-bold px-2.5 py-1 rounded-full" data-id="element-1569">
              {accreditationRequests.length} New
            </span>
          </div>

          <div className="relative" data-id="element-1570">
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 p-5" data-id="element-1571">
              {/* Request Card */}
              <div className="flex items-start gap-4 mb-4" data-id="element-1572">
                <div className="w-14 h-14 rounded-xl bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 shadow-sm p-2" data-id="element-1573">
                  <img src={currentRequest.brokerageLogo} alt={currentRequest.brokerageName} className="w-full h-full object-contain" data-id="element-1574" />
                </div>
                <div className="flex-1" data-id="element-1575">
                  <h3 className="text-lg font-bold text-neutral-text mb-1" data-id="element-1576">
                    {currentRequest.brokerageName}
                  </h3>
                  <p className="text-xs text-gray-500 mb-1" data-id="element-1577">
                    {currentRequest.contactPerson}
                  </p>
                  <p className="text-xs text-gray-500 mb-2" data-id="element-1578">
                    {currentRequest.email}
                  </p>
                  <div className="flex items-center gap-2 mb-3 flex-wrap" data-id="element-1579">
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${getTierBadgeColor(currentRequest.requestedTier)}`} data-id="element-1580">
                      {currentRequest.requestedTier.toUpperCase()} PARTNERSHIP
                    </span>
                    <span className="text-xs text-gray-400" data-id="element-1581">
                      {currentRequest.requestDate}
                    </span>
                  </div>
                </div>
              </div>

              {/* Request Details */}
              <div className="bg-gray-50 rounded-xl p-4 mb-4" data-id="element-1582">
                <div className="grid grid-cols-2 gap-4 mb-3" data-id="element-1583">
                  <div data-id="element-1584">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1" data-id="element-1585">
                      <Users className="w-3 h-3" data-id="element-1586" />
                      Agents
                    </p>
                    <p className="text-xl font-bold text-primary" data-id="element-1587">
                      {currentRequest.agentsCount}
                    </p>
                  </div>
                  <div data-id="element-1588">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1" data-id="element-1589">
                      <TrendingUp className="w-3 h-3" data-id="element-1590" />
                      Deals Closed
                    </p>
                    <p className="text-xl font-bold text-primary" data-id="element-1591">
                      {currentRequest.dealsCompleted}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-700 italic" data-id="element-1592">
                  "{currentRequest.message}"
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3" data-id="element-1593">
                <button onClick={() => handleApproveRequest(currentRequest.id)} className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-1594">
                  <CheckCircle className="w-5 h-5" data-id="element-1595" />
                  Approve
                </button>
                <button onClick={() => handleRejectRequest(currentRequest.id)} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-1596">
                  <XCircle className="w-5 h-5" data-id="element-1597" />
                  Reject
                </button>
              </div>
            </div>

            {/* Slider dots */}
            <div className="flex justify-center gap-2 mt-4" data-id="element-1598">
              {accreditationRequests.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`w-2 h-2 rounded-full transition-colors ${index === currentSlide ? 'bg-primary' : 'bg-gray-300'}`} data-id="element-1599" />)}
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-4 pb-4" data-id="element-1600">
          <div className="relative" data-id="element-1601">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1602" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by Brokerage Name or Specialization" className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent" data-id="element-1603" />
          </div>
        </div>

        {/* Suggested Brokerages Section - Horizontal Scroll */}
        <div className="pb-6" data-id="element-1604">
          <h2 className="text-base font-bold text-neutral-text mb-4 px-4" data-id="element-1605">
            Suggested Brokerages
          </h2>
          <div className="overflow-x-auto no-scrollbar" data-id="element-1606">
            <div className="flex gap-3 px-4" data-id="element-1607">
              {suggestedBrokerages.map(brokerage => <div key={brokerage.id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden relative flex-shrink-0 w-[220px]" data-id="element-1608">
                  {/* Brokerage Header */}
                  <div className="p-4 flex flex-col items-center border-b border-gray-100 bg-gradient-to-br from-gray-50 to-white" data-id="element-1609">
                    <div className="w-16 h-16 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow-sm mb-3 p-2" data-id="element-1610">
                      <img src={brokerage.logo} alt={brokerage.name} className="w-full h-full object-contain" data-id="element-1611" />
                    </div>
                    <h3 className="font-bold text-sm text-neutral-text text-center mb-1 line-clamp-2 min-h-[2.5rem]" data-id="element-1612">
                      {brokerage.name}
                    </h3>
                    <p className="text-xs text-gray-500 text-center mb-2" data-id="element-1613">
                      {brokerage.specialization}
                    </p>
                  </div>

                  {/* Content */}
                  <div className="p-4" data-id="element-1614">
                    <div className="grid grid-cols-2 gap-2 mb-3" data-id="element-1615">
                      <div data-id="element-1616">
                        <p className="text-xs text-gray-500" data-id="element-1617">Agents</p>
                        <p className="text-sm font-bold text-primary" data-id="element-1618">
                          {brokerage.agentsCount}
                        </p>
                      </div>
                      <div data-id="element-1619">
                        <p className="text-xs text-gray-500" data-id="element-1620">Deals</p>
                        <p className="text-sm font-bold text-primary" data-id="element-1621">
                          {brokerage.dealsCount}
                        </p>
                      </div>
                    </div>

                    <div className="mb-3" data-id="element-1622">
                      <p className="text-xs text-gray-500 mb-2" data-id="element-1623">Memberships</p>
                      {getMembershipBadges(brokerage.memberships)}
                    </div>

                    {/* Action Button */}
                    <button onClick={() => onBrokerageProfileClick && onBrokerageProfileClick(brokerage.id, brokerage.name, false)} className="w-full flex items-center justify-center gap-1 text-xs font-medium text-secondary hover:opacity-80 transition-opacity py-2 bg-secondary/10 rounded-lg" data-id="element-1624">
                      <Building className="w-3.5 h-3.5" data-id="element-1625" />
                      <span data-id="element-1626">View Profile</span>
                    </button>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* My Brokerages Section */}
        <div className="px-4 pb-6" data-id="element-1627">
          <h2 className="text-base font-bold text-neutral-text mb-4" data-id="element-1628">
            My Brokerages
          </h2>
          <div className="grid grid-cols-1 gap-3" data-id="element-1629">
            {myBrokerages.map(brokerage => <div key={brokerage.id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden p-4" data-id="element-1630">
                <div className="flex items-start gap-4" data-id="element-1631">
                  {/* Logo */}
                  <div className="w-14 h-14 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow-sm flex-shrink-0 p-2" data-id="element-1632">
                    <img src={brokerage.logo} alt={brokerage.name} className="w-full h-full object-contain" data-id="element-1633" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0" data-id="element-1634">
                    <div className="flex items-start justify-between mb-2" data-id="element-1635">
                      <div data-id="element-1636">
                        <h3 className="font-bold text-base text-neutral-text mb-1" data-id="element-1637">
                          {brokerage.name}
                        </h3>
                        <p className="text-xs text-gray-500 mb-1" data-id="element-1638">
                          {brokerage.specialization}
                        </p>
                        <p className="text-xs text-gray-400 mb-2" data-id="element-1639">
                          Est. {brokerage.establishedYear}
                        </p>
                      </div>
                      <button onClick={() => onBrokerageProfileClick && onBrokerageProfileClick(brokerage.id, brokerage.name, true)} className="p-2 text-secondary hover:bg-secondary/10 rounded-full transition-colors" data-id="element-1640">
                        <ArrowUpRight className="w-5 h-5" data-id="element-1641" />
                      </button>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-4 mb-3" data-id="element-1642">
                      <div data-id="element-1643">
                        <p className="text-xs text-gray-500" data-id="element-1644">Agents</p>
                        <p className="text-sm font-bold text-primary" data-id="element-1645">
                          {brokerage.agentsCount}
                        </p>
                      </div>
                      <div data-id="element-1646">
                        <p className="text-xs text-gray-500" data-id="element-1647">Deals</p>
                        <p className="text-sm font-bold text-primary" data-id="element-1648">
                          {brokerage.dealsCount}
                        </p>
                      </div>
                      <div data-id="element-1649">
                        <p className="text-xs text-gray-500" data-id="element-1650">Memberships</p>
                        <p className="text-sm font-bold text-primary" data-id="element-1651">
                          {brokerage.totalMemberships}
                        </p>
                      </div>
                    </div>

                    {/* Membership Badges */}
                    <div data-id="element-1652">
                      <p className="text-xs text-gray-500 mb-2" data-id="element-1653">
                        Partnership Tiers
                      </p>
                      {getMembershipBadges(brokerage.memberships)}
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        <BottomNav onNetworkClick={onNetworkClick} onInventoryClick={onInventoryClick} onRMIClick={onRMIClick} onProfileClick={onProfileClick} activeNav="network" data-id="element-1654" />
      </div>
    </div>;
}